import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShieldCheck, Copy, Check, AlertTriangle, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

// AUTH-01 — MFA setup (rendered inside StandaloneShell)
const FAKE_SECRET = "JBSWY3DPEHPK3PXP";
const RECOVERY_CODES = [
  "K3NX-9PRB-Q72M", "VLMA-1HSC-WZ04", "P9TF-XR8E-BM6N",
  "C2QV-NL5J-FDA8", "T7HK-WRYP-S0EX", "B3UZ-KFJM-O4Q1",
];

export default function MfaSetupPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [code, setCode] = useState("");
  const [secretCopied, setSecretCopied] = useState(false);
  const [codesAcked, setCodesAcked] = useState(false);

  const verify = () => {
    if (code.length === 6) {
      setStep(3);
    } else {
      toast.error("Enter all 6 digits");
    }
  };

  const finish = () => {
    toast.success("Two-factor authentication enabled");
    setTimeout(() => navigate("/auth/mfa-success"), 600);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="size-10 rounded-md bg-brand-50 text-brand-700 grid place-items-center">
          <ShieldCheck className="size-5" />
        </div>
        <div>
          <div className="eyebrow text-brand-700">Step {step} of 3</div>
          <h1 className="font-brand text-h1 text-ink">Set up two-factor</h1>
        </div>
      </div>

      {/* Stepper */}
      <ol className="flex items-center gap-2 text-caption">
        <Step n={1} active={step === 1} done={step > 1} label="Scan" />
        <span className="flex-1 h-px bg-line" />
        <Step n={2} active={step === 2} done={step > 2} label="Verify" />
        <span className="flex-1 h-px bg-line" />
        <Step n={3} active={step === 3} done={false} label="Backup" />
      </ol>

      {step === 1 && (
        <div className="space-y-4">
          <p className="text-body-sm text-ink-muted leading-relaxed">
            Open your authenticator app (1Password, Authy, Google Authenticator, etc.) and scan the QR code below.
            If you can&rsquo;t scan, paste the secret manually.
          </p>
          <div className="bg-surface-alt border border-line rounded-md p-6 grid place-items-center">
            <FakeQR />
          </div>
          <div>
            <Label className="text-caption font-semibold text-ink mb-1.5 block">Or paste this secret</Label>
            <div className="flex gap-2">
              <Input value={FAKE_SECRET} readOnly className="font-mono text-code tabular-nums" />
              <Button variant="outline" onClick={() => { navigator.clipboard?.writeText(FAKE_SECRET); setSecretCopied(true); }}>
                {secretCopied ? <Check className="size-4" /> : <Copy className="size-4" />}
              </Button>
            </div>
          </div>
          <Button onClick={() => setStep(2)} className="w-full h-11 bg-brand-700 hover:bg-brand-800 text-white" data-testid="mfa-next-1">
            I&rsquo;ve scanned · Continue <ArrowRight className="size-4 ml-2" />
          </Button>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          <p className="text-body-sm text-ink-muted leading-relaxed">
            Enter the 6-digit code from your authenticator. Codes refresh every 30 seconds.
          </p>
          <div>
            <Label className="text-caption font-semibold text-ink mb-1.5 block">6-digit code</Label>
            <Input
              autoFocus inputMode="numeric" maxLength={6} pattern="\d{6}"
              value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="••• •••" className="text-center font-mono text-h2 tabular-nums tracking-[0.5em] h-14"
              data-testid="mfa-code-input"
            />
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setStep(1)} className="flex-1">Back</Button>
            <Button onClick={verify} disabled={code.length < 6} data-testid="mfa-verify-btn"
                    className="flex-1 bg-brand-700 hover:bg-brand-800 text-white">Verify</Button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-4">
          <div className="bg-amber-50/40 border border-amber-200 rounded-md p-4 flex items-start gap-3">
            <AlertTriangle className="size-4 text-amber-700 mt-0.5 shrink-0" />
            <p className="text-caption text-ink-muted leading-relaxed">
              Save these recovery codes somewhere safe (a password manager works well). Each code can be used <strong>once</strong> if you lose access to your authenticator.
            </p>
          </div>
          <div className="bg-surface-alt border border-line rounded-md p-5">
            <ul className="grid grid-cols-2 gap-x-6 gap-y-2 font-mono text-code text-ink tabular-nums">
              {RECOVERY_CODES.map((c) => <li key={c} className="select-all">{c}</li>)}
            </ul>
          </div>
          <Button variant="outline" onClick={() => { navigator.clipboard?.writeText(RECOVERY_CODES.join("\n")); toast.success("Codes copied"); }} className="w-full">
            <Copy className="size-4 mr-1.5" /> Copy all codes
          </Button>
          <label className="flex items-start gap-2 text-caption text-ink-muted leading-relaxed">
            <input type="checkbox" checked={codesAcked} onChange={(e) => setCodesAcked(e.target.checked)} className="mt-0.5" />
            <span>I&rsquo;ve saved my recovery codes somewhere I can find them.</span>
          </label>
          <Button onClick={finish} disabled={!codesAcked} className="w-full h-11 bg-brand-700 hover:bg-brand-800 text-white" data-testid="mfa-finish-btn">
            Finish · Enable 2FA
          </Button>
        </div>
      )}

      <div className="border-t border-line pt-4 text-center text-caption text-ink-subtle">
        <Link to="/member/account" className="hover:text-ink">Cancel and go back</Link>
      </div>
    </div>
  );
}

function Step({ n, active, done, label }) {
  return (
    <li className="flex items-center gap-1.5 shrink-0">
      <span className={`size-6 rounded-full grid place-items-center text-[10px] font-bold tabular-nums ${
        done ? "bg-brand-700 text-white" :
        active ? "bg-brand-50 text-brand-800 ring-2 ring-brand-200" :
                 "bg-surface text-ink-hint border border-line"
      }`}>
        {done ? <Check className="size-3" strokeWidth={3} /> : n}
      </span>
      <span className={active ? "text-ink font-semibold" : "text-ink-subtle"}>{label}</span>
    </li>
  );
}

function FakeQR() {
  // Procedurally drawn pseudo-QR with brand teal
  const cells = 21;
  const pattern = Array.from({ length: cells * cells }, (_, i) => {
    const x = i % cells, y = Math.floor(i / cells);
    // Position detector squares (corners)
    const corner =
      (x < 7 && y < 7) || (x >= cells - 7 && y < 7) || (x < 7 && y >= cells - 7);
    if (corner) {
      const lx = x % 7, ly = y % 7;
      return (lx === 0 || lx === 6 || ly === 0 || ly === 6 || (lx > 1 && lx < 5 && ly > 1 && ly < 5));
    }
    // pseudo-random pattern (deterministic)
    return ((x * 7 + y * 13 + 17) % 5 < 2);
  });
  return (
    <div className="grid bg-surface p-3 rounded-md" style={{ gridTemplateColumns: `repeat(${cells}, 8px)` }}>
      {pattern.map((on, i) => (
        <span key={i} style={{ width: 8, height: 8, background: on ? "#0F172A" : "transparent" }} />
      ))}
    </div>
  );
}
